import type { SessionChargeId } from '@nesso/ledger-contracts/ids';
import { Button } from '@nesso/ui/primitives/button';
import { Separator } from '@nesso/ui/primitives/separator';

import { BillingHistory } from '@/entities/billing-history';
import { formatPaymentNumber, type Payment } from '@/entities/payment';
import type { SessionCharge } from '@/entities/session-charge';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericShortDate } from '@/shared/lib/time-utils';

type Props = {
    sessionCharge: SessionCharge;
    pagePayments: Payment[];
    onAddPayment: (sessionChargeId: SessionChargeId) => void;
    onOpenPayment: (payment: Payment, sessionChargeId: SessionChargeId) => void;
    onRefund: (payment: Payment) => void;
    onEditFee: (sessionCharge: SessionCharge) => void;
};

export function ClientBillingSheetBillingDetails({
    sessionCharge,
    pagePayments,
    onAddPayment,
    onOpenPayment,
    onRefund,
    onEditFee,
}: Props) {
    return (
        <div className="flex flex-col gap-4 text-sm">
            <section className="flex flex-col gap-1">
                <h4 className="font-bold">Billing details</h4>
                <dl className="flex flex-col">
                    <div className="flex items-start justify-between">
                        <dt>Fee</dt>
                        <dd>{formatCurrencyFromCents(sessionCharge.feeCents)}</dd>
                    </div>
                    <div className="flex items-start justify-between">
                        <dt>Balance</dt>
                        <dd>{formatCurrencyFromCents(sessionCharge.balanceCents)}</dd>
                    </div>
                </dl>
                <div className="flex items-center gap-2 pt-0.5">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7"
                        disabled={sessionCharge.balanceCents === 0}
                        onClick={() => onAddPayment(sessionCharge.id)}
                    >
                        Add Payment
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7"
                        onClick={() => onEditFee(sessionCharge)}
                    >
                        Edit Fee
                    </Button>
                </div>
            </section>
            <section className="flex flex-col gap-1">
                <h4 className="font-bold">Transactions</h4>
                {sessionCharge.payments.length === 0 ? (
                    <p>No transactions yet</p>
                ) : (
                    <ul aria-label="Transactions" className="flex flex-col gap-0.5">
                        {sessionCharge.payments.map((sessionChargePayment) => {
                            const pagePayment = pagePayments.find(
                                (candidate) => candidate.id === sessionChargePayment.paymentId,
                            );
                            const paymentNumber = formatPaymentNumber(sessionChargePayment.number);

                            return (
                                <li
                                    key={sessionChargePayment.paymentId}
                                    className="flex items-center justify-between"
                                >
                                    <p
                                        className={
                                            pagePayment?.refund ? 'flex line-through' : undefined
                                        }
                                    >
                                        {pagePayment ? (
                                            <Button
                                                type="button"
                                                variant="inline-link"
                                                className="decoration-from-font underline-offset-auto [text-decoration-skip-ink:none] [text-underline-position:from-font]"
                                                aria-label={`Show details for ${paymentNumber}`}
                                                onClick={() =>
                                                    onOpenPayment(pagePayment, sessionCharge.id)
                                                }
                                            >
                                                {paymentNumber}
                                            </Button>
                                        ) : (
                                            paymentNumber
                                        )}
                                        {`, ${formatCurrencyFromCents(sessionChargePayment.amountCents)} on ${formatNumericShortDate(sessionChargePayment.paidAt)}`}
                                    </p>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="-mr-2.5"
                                        disabled={!pagePayment || Boolean(pagePayment.refund)}
                                        aria-label={`Refund ${paymentNumber}`}
                                        onClick={() => pagePayment && onRefund(pagePayment)}
                                    >
                                        Refund
                                    </Button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
            <Separator />
            <section className="flex flex-col gap-2">
                <h4 className="font-bold">History</h4>
                <BillingHistory entries={sessionCharge.history} />
            </section>
        </div>
    );
}
